// src/components/ScentQuiz.js
// Six light-hearted questions that map onto real scent families (see
// data/scentProfiles.js) and how loud the customer wants to be. Each answer
// gets a short reaction before moving on, and the result shows the customer's
// scent profile with a Best Match plus an Alternative Match.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    ArrowRight, RotateCcw, ChevronLeft, ShoppingBag,
    CakeSlice, Cookie, Cherry, Coffee, Flower2, Sofa, Martini, Snowflake,
    TreePalm, Building2, Map as MapIcon, Crown, Heart, Smile, Sparkles, Sunrise,
    Briefcase, PartyPopper, Megaphone, MessageCircleQuestion, Wine, Glasses,
} from 'lucide-react';
import { getScent } from '../data/scents';
import { SCENT_PROFILES as PROFILES } from '../data/scentProfiles';
import { useCurrency } from '../context/CurrencyContext';
import { useCart } from '../context/CartContext';
import { trackQuizResult } from '../lib/analytics';

const IC = "w-6 h-6 text-[#CEAC8F]";

// How long each answer's reaction stays on screen before the next question
const REACTION_MS = 1400;

const QUESTIONS = [
    {
        question: "First things first. Pick a dessert.",
        answers: [
            { text: "Tiramisu, obviously",                   icon: <CakeSlice className={IC} />, reaction: "A person of taste. Coffee noted.",                scores: { beirut: 3, geneva: 1 } },
            { text: "A cinnamon roll, still warm",           icon: <Cookie className={IC} />,    reaction: "Soft-hearted. We respect it.",                    scores: { geneva: 3, prague: 2 } },
            { text: "Fruit sorbet, extra fruit",             icon: <Cherry className={IC} />,    reaction: "Bright, juicy, zero regrets. Got it.",            scores: { damascus: 3, seville: 2, 'sao-paulo': 1 } },
            { text: "90% dark chocolate. I don't do sweet.", icon: <Coffee className={IC} />,    reaction: "Intense. Slightly scary. Writing that down.",     scores: { medina: 3, prague: 1, marrakech: 1 } },
        ],
    },
    {
        question: "Your ideal Saturday looks like…",
        answers: [
            { text: "Flower market by 10am, flowers in hand",     icon: <Flower2 className={IC} />, reaction: "Fresh air and good intentions. Lovely.",          scores: { seville: 3, damascus: 1 } },
            { text: "Brunch that somehow turns into dinner",      icon: <Wine className={IC} />,    reaction: "The friend everyone wants at the table.",         scores: { damascus: 3, 'sao-paulo': 2, geneva: 1 } },
            { text: "Blanket, candle, a film I've seen 11 times", icon: <Sofa className={IC} />,    reaction: "Comfort is a personality. Noted.",                scores: { geneva: 3, beirut: 1 } },
            { text: "Rooftop bar, home at 3am, no regrets",       icon: <Martini className={IC} />, reaction: "We won't tell anyone. Your scent might.",         scores: { prague: 3, beirut: 2, marrakech: 1 } },
        ],
    },
    {
        question: "Pick a holiday. Budget is not your problem.",
        answers: [
            { text: "Getting lost in a spice market, on purpose", icon: <MapIcon className={IC} />,       reaction: "Saffron in the air. You'd fit right in.",        scores: { marrakech: 3, medina: 1 } },
            { text: "A pool, a book, absolutely zero plans",       icon: <TreePalm className={IC} />,  reaction: "Sun-warmed and unbothered. Perfect.",            scores: { damascus: 3, seville: 2 } },
            { text: "A cabin in the snow with a fireplace",        icon: <Snowflake className={IC} />, reaction: "Warm inside, cold outside. Very cosy of you.",   scores: { prague: 3, geneva: 2, medina: 1 } },
            { text: "A city that never sleeps",                    icon: <Building2 className={IC} />, reaction: "Late nights and later breakfasts. Got it.",       scores: { beirut: 3, 'sao-paulo': 2, marrakech: 1 } },
        ],
    },
    {
        question: "You walk into a room. What do people think?",
        answers: [
            { text: "\"Who is THAT?\"",                         icon: <Crown className={IC} />,                 reaction: "Big entrance energy. We have just the thing.",  scores: { medina: 3, prague: 1, marrakech: 1 } },
            { text: "\"You smell like a hug.\"",                icon: <Heart className={IC} />,                 reaction: "The highest compliment there is.",              scores: { geneva: 3, beirut: 1 } },
            { text: "\"Why are you in such a good mood?\"",      icon: <Smile className={IC} />,                 reaction: "Your scent is doing half the work.",            scores: { damascus: 3, seville: 2 } },
            { text: "Nothing. They ask about it three days later.", icon: <MessageCircleQuestion className={IC} />, reaction: "The slow burn. Very sophisticated.",            scores: { prague: 3, seville: 2, marrakech: 1 } },
        ],
    },
    {
        question: "When are you actually wearing this?",
        answers: [
            { text: "Mornings, coffee in hand, already late",   icon: <Sunrise className={IC} />,     reaction: "Fresh enough to hide the lateness.",            scores: { seville: 3, damascus: 2 } },
            { text: "Office, pretending to be a functional adult", icon: <Briefcase className={IC} />, reaction: "Professional, but make it smell nice.",         scores: { 'sao-paulo': 3, seville: 1, geneva: 1, damascus: 1 } },
            { text: "Date night",                               icon: <Glasses className={IC} />,     reaction: "Say less. We've got you.",                      scores: { beirut: 3, prague: 2, medina: 1 } },
            { text: "Weddings, events, anything with a dress code", icon: <PartyPopper className={IC} />, reaction: "Dress code: unforgettable.",                scores: { marrakech: 3, medina: 2 } },
        ],
    },
    {
        question: "Be honest. How many compliments do you want?",
        answers: [
            { text: "A polite amount",                          icon: <Sparkles className={IC} />,  reaction: "Modest. Suspiciously modest.",                  scores: { seville: 2, geneva: 1, damascus: 1 } },
            { text: "Enough to feel good about myself",         icon: <Smile className={IC} />,     reaction: "Healthy. Balanced. Rare.",                      scores: { 'sao-paulo': 3, damascus: 2, beirut: 1 } },
            { text: "All of them. Every single one.",           icon: <Megaphone className={IC} />, reaction: "Honesty. We love that for you.",                scores: { medina: 3, marrakech: 2 } },
            { text: "I don't want compliments, I want questions", icon: <MessageCircleQuestion className={IC} />, reaction: "Mysterious. Okay, we see you.", scores: { prague: 3, marrakech: 1, beirut: 1 } },
        ],
    },
];

const LOADING_LINES = [
    'Sniffing your answers…',
    'Consulting the nose…',
    'Arguing about it internally…',
    'Found it.',
];

// Highest score each scent could possibly get, so scents that appear in more
// answers don't win just by showing up more often.
const MAX_SCORES = QUESTIONS.reduce((max, q) => {
    Object.keys(PROFILES).forEach((handle) => {
        max[handle] = (max[handle] || 0) + Math.max(0, ...q.answers.map(a => a.scores[handle] || 0));
    });
    return max;
}, {});

// Every scent, ranked by how much of its possible score the answers gave it.
// A scent no answer scores yet (e.g. just added to PROFILES) is left out —
// its possible score is 0, so it can't be ranked.
const rankHandles = (answers) => {
    const totals = {};
    answers.forEach((answer) => {
        if (!answer) return;
        Object.entries(answer.scores).forEach(([handle, points]) => {
            totals[handle] = (totals[handle] || 0) + points;
        });
    });
    return Object.keys(PROFILES)
        .filter(handle => MAX_SCORES[handle] > 0)
        .map(handle => ({ handle, total: totals[handle] || 0, ratio: (totals[handle] || 0) / MAX_SCORES[handle] }))
        .sort((a, b) => b.ratio - a.ratio || b.total - a.total)
        .map(({ handle }) => handle);
};

const notesLine = (product) =>
    [...(product.topNotes || []), ...(product.heartNotes || []), ...(product.baseNotes || [])].join(', ');

const ScentQuiz = () => {
    const { country, symbol } = useCurrency();
    const { addToCart, loading: cartLoading } = useCart();

    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [ranked, setRanked] = useState(null);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingLine, setLoadingLine] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [reaction, setReaction] = useState(null);

    const progress = (currentQuestion / QUESTIONS.length) * 100;

    // Fetches the ranked scents from Shopify and keeps the top two, preferring
    // in-stock ones so the Best Match is always something they can buy.
    const pickResults = async (handles) => {
        const products = (await Promise.all(handles.map(handle => getScent(handle, country)))).filter(Boolean);
        const available = products.filter(p => p.availableForSale);
        const unavailable = products.filter(p => !p.availableForSale);
        return [...available, ...unavailable].slice(0, 2);
    };

    // Re-price the results if the visitor switches currency on the results screen
    useEffect(() => {
        if (ranked && results) {
            pickResults(ranked).then(setResults).catch(() => {});
        }
    }, [country]);

    // Cycle the loading lines while results are being fetched
    useEffect(() => {
        if (!loading) return;
        setLoadingLine(0);
        const id = setInterval(() => {
            setLoadingLine(i => Math.min(i + 1, LOADING_LINES.length - 1));
        }, 600);
        return () => clearInterval(id);
    }, [loading]);

    const handleAnswer = async (answer) => {
        if (reaction) return;
        setSelectedAnswer(answer.text);
        setReaction(answer.reaction);

        const newAnswers = [...answers];
        newAnswers[currentQuestion] = answer;
        setAnswers(newAnswers);

        await new Promise(r => setTimeout(r, REACTION_MS));
        setSelectedAnswer(null);
        setReaction(null);

        if (currentQuestion < QUESTIONS.length - 1) {
            setCurrentQuestion(prev => prev + 1);
        } else {
            const handles = rankHandles(newAnswers);
            setRanked(handles);
            await showResults(handles);
        }
    };

    const showResults = async (handles) => {
        setLoading(true);
        // Let the loading lines play out — the joke needs a beat.
        const minDelay = new Promise(r => setTimeout(r, LOADING_LINES.length * 600));
        let shown;
        try {
            [shown] = await Promise.all([pickResults(handles), minDelay]);
        } catch (err) {
            await minDelay;
            shown = handles.slice(0, 2).map(handle => ({
                handle,
                name: handle.charAt(0).toUpperCase() + handle.slice(1),
                price: null,
            }));
        }
        setResults(shown);
        try {
            // Record what was actually shown (an out-of-stock top scent can be
            // swapped out), so the profile always matches the Best Match.
            const [best, alt] = shown;
            trackQuizResult({
                profile: PROFILES[best?.handle]?.profile,
                bestMatch: best?.name,
                alternativeMatch: alt?.name,
                bestHandle: best?.handle,
                alternativeHandle: alt?.handle,
            });
        } finally {
            setLoading(false);
        }
    };

    const goBack = () => {
        if (currentQuestion === 0 || reaction) return;
        setSelectedAnswer(null);
        setCurrentQuestion(prev => prev - 1);
    };

    const resetQuiz = () => {
        setCurrentQuestion(0);
        setAnswers([]);
        setRanked(null);
        setResults(null);
        setSelectedAnswer(null);
        setReaction(null);
    };

    const question = QUESTIONS[currentQuestion];
    const chosenForThisQuestion = answers[currentQuestion]?.text;

    return (
        <section className="py-24 px-6 bg-background transition-colors duration-300">
            <div className="max-w-3xl mx-auto">

                <div className="text-center mb-12">
                    <h2 className="text-4xl md:text-5xl font-light text-foreground mb-4 tracking-wide">
                        Find Your Scent
                    </h2>
                    <p className="text-muted-foreground text-lg">
                        Six questions. Zero judgement. One very good match.
                    </p>
                </div>

                <div className="bg-card rounded-2xl border border-border overflow-hidden">

                    {!results && (
                        <div className="h-1 bg-border">
                            <div
                                className="h-full bg-[#CEAC8F] transition-all duration-500"
                                style={{ width: `${loading ? 100 : progress}%` }}
                            />
                        </div>
                    )}

                    <div className="p-8 sm:p-10">

                        {loading && (
                            <div className="text-center py-12">
                                <div key={loadingLine} className="animate-pulse text-muted-foreground tracking-wide">
                                    {LOADING_LINES[loadingLine]}
                                </div>
                            </div>
                        )}

                        {!results && !loading && (
                            <>
                                <div className="mb-8 flex items-start gap-3">
                                    {currentQuestion > 0 && (
                                        <button
                                            onClick={goBack}
                                            aria-label="Previous question"
                                            className="text-muted-foreground hover:text-[#CEAC8F] transition-colors mt-1 -ml-1"
                                        >
                                            <ChevronLeft className="w-5 h-5" />
                                        </button>
                                    )}
                                    <div>
                                        <p className="text-[#CEAC8F] text-xs tracking-widest uppercase mb-3">
                                            Question {currentQuestion + 1} of {QUESTIONS.length}
                                        </p>
                                        <h3 className="text-2xl sm:text-3xl font-light text-foreground">
                                            {question.question}
                                        </h3>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {question.answers.map((answer) => {
                                        const isSelected = selectedAnswer === answer.text
                                            || (!reaction && chosenForThisQuestion === answer.text);
                                        return (
                                            <button
                                                key={answer.text}
                                                onClick={() => handleAnswer(answer)}
                                                disabled={!!reaction}
                                                className={`text-left p-5 rounded-xl border transition-all duration-200 text-foreground ${
                                                    isSelected
                                                        ? 'border-[#CEAC8F] bg-[#CEAC8F]/10 scale-[1.02]'
                                                        : reaction
                                                            ? 'border-border opacity-40'
                                                            : 'border-border hover:border-[#CEAC8F]/50 hover:bg-[#CEAC8F]/5'
                                                }`}
                                            >
                                                <span className="mb-3 block">{answer.icon}</span>
                                                <span className="text-sm font-light leading-snug">{answer.text}</span>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Reaction to the chosen answer — fixed height so the
                                    card doesn't jump when it appears. */}
                                <div className="h-8 mt-6 text-center" aria-live="polite">
                                    {reaction && (
                                        <p className="text-[#CEAC8F] italic animate-in fade-in slide-in-from-bottom-2 duration-300">
                                            {reaction}
                                        </p>
                                    )}
                                </div>
                            </>
                        )}

                        {results && !loading && (
                            <>
                                <div className="text-center mb-8">
                                    <p className="text-[#CEAC8F] text-xs tracking-widest uppercase mb-3">
                                        The Verdict
                                    </p>
                                    <h3 className="text-2xl sm:text-3xl font-light text-foreground mb-2">
                                        {PROFILES[results[0]?.handle]
                                            ? `You're ${PROFILES[results[0].handle].profile}.`
                                            : 'Your Match'}
                                    </h3>
                                    <p className="text-muted-foreground text-sm">
                                        We've thought about it far more than is reasonable. Here's your scent.
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                                    {results.map((product, i) => {
                                        const profile = PROFILES[product.handle];
                                        const notes = notesLine(product);
                                        return (
                                            <div
                                                key={product.handle}
                                                className={`bg-background rounded-xl overflow-hidden flex flex-col border ${
                                                    i === 0 ? 'border-[#CEAC8F]' : 'border-border'
                                                }`}
                                            >
                                                <Link to={`/product/${product.handle}`} className="group block">
                                                    {product.image && (
                                                        <div className="aspect-[3/4] overflow-hidden">
                                                            <img
                                                                src={product.image}
                                                                alt={product.name}
                                                                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
                                                            />
                                                        </div>
                                                    )}
                                                    <div className="px-4 pt-4">
                                                        <p className="text-[#CEAC8F] text-xs tracking-widest uppercase mb-1">
                                                            {i === 0 ? 'Best Match' : 'Alternative Match'}
                                                        </p>
                                                        <h4 className="text-foreground font-light text-lg group-hover:text-[#CEAC8F] transition-colors">
                                                            {product.name}
                                                        </h4>
                                                        {profile && (
                                                            <p className="text-muted-foreground/70 text-[10px] tracking-widest uppercase mb-2">
                                                                {profile.profile} · 12ml
                                                            </p>
                                                        )}
                                                        {product.price && (
                                                            <p className="text-[#CEAC8F] mb-3">
                                                                {symbol}{product.price.toFixed(2)}
                                                            </p>
                                                        )}
                                                    </div>
                                                </Link>

                                                <div className="px-4 pb-4 flex-1 flex flex-col">
                                                    {profile && (
                                                        <p className="text-foreground text-sm leading-relaxed mb-3">
                                                            {i === 0 ? profile.tagline : `The other side of you. ${profile.tagline}`}
                                                        </p>
                                                    )}
                                                    {notes && (
                                                        <p className="text-muted-foreground text-xs leading-relaxed mb-4">
                                                            <span className="text-[#CEAC8F]/70 uppercase tracking-widest text-[10px] block mb-1">Notes</span>
                                                            {notes}
                                                        </p>
                                                    )}
                                                    {product.variantId && product.availableForSale && (
                                                        <button
                                                            onClick={() => addToCart(product.variantId)}
                                                            disabled={cartLoading}
                                                            className={`mt-auto flex items-center justify-center gap-2 py-3 rounded-lg text-sm font-medium transition-all disabled:opacity-60 ${
                                                                i === 0
                                                                    ? 'bg-[#CEAC8F] text-black hover:bg-[#d4b896]'
                                                                    : 'border border-[#CEAC8F] text-[#CEAC8F] hover:bg-[#CEAC8F] hover:text-black'
                                                            }`}
                                                        >
                                                            <ShoppingBag className="w-4 h-4" />
                                                            Add to Cart
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="flex flex-col sm:flex-row gap-3">
                                    <Link
                                        to="/scents"
                                        className="flex-1 flex items-center justify-center gap-2 border border-border text-foreground py-4 rounded-lg font-medium hover:border-[#CEAC8F]/50 transition-all hover:gap-4"
                                    >
                                        Shop All Scents
                                        <ArrowRight className="w-5 h-5" />
                                    </Link>
                                    <button
                                        onClick={resetQuiz}
                                        className="flex items-center justify-center gap-2 px-6 py-4 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-[#CEAC8F]/30 transition-all text-sm"
                                    >
                                        <RotateCcw className="w-4 h-4" />
                                        Retake Quiz
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ScentQuiz;
